import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Autorenew, Info } from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    backgroundColor: "#FFF3E0",
    borderBottom: `2px solid #FF6F00`,
  },
  icon: {
    color: "#FF6F00",
    width: 28,
    height: 28,
  },
  content: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  helperText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  previewBox: {
    backgroundColor: "#F5F5F5",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
  infoBox: {
    backgroundColor: "#FFF3E0",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid #FFB74D`,
    marginTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1),
  },
  variableChip: {
    backgroundColor: "rgba(255, 111, 0, 0.15)",
    color: "#FF6F00",
    padding: "2px 8px",
    borderRadius: 4,
    fontFamily: "monospace",
    fontSize: "12px",
  },
  row: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-start",
  },
}));

const operators = [
  { value: "equals_to", label: "Igual a (=)" },
  { value: "not_equals_to", label: "Diferente de (≠)" },
  { value: "greater_than", label: "Maior que (>)" },
  { value: "less_than", label: "Menor que (<)" },
  { value: "greater_or_equal", label: "Maior ou igual (≥)" },
  { value: "less_or_equal", label: "Menor ou igual (≤)" },
  { value: "contains", label: "Contém" },
  { value: "not_contains", label: "Não contém" },
  { value: "starts_with", label: "Começa com" },
  { value: "ends_with", label: "Termina com" },
  { value: "is_empty", label: "Está vazio" },
  { value: "is_not_empty", label: "Não está vazio" },
  { value: "isTrue", label: "É verdadeiro" },
  { value: "isFalse", label: "É falso" },
];

const FlowBuilderAddWhileModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const [variable, setVariable] = useState("");
  const [operator, setOperator] = useState("equals_to");
  const [value, setValue] = useState("");
  const [maxIterations, setMaxIterations] = useState(100);

  useEffect(() => {
    if (open === "edit" && data?.data) {
      setVariable(data.data.variable || "");
      setOperator(data.data.operator || "equals_to");
      setValue(data.data.value || "");
      setMaxIterations(data.data.maxIterations || 100);
    } else if (open === "create") {
      setVariable("");
      setOperator("equals_to");
      setValue("");
      setMaxIterations(100);
    }
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setVariable("");
    setOperator("equals_to");
    setValue("");
    setMaxIterations(100);
  };

  const handleSave = () => {
    if (!variable.trim()) {
      alert("Por favor, defina a variável a ser avaliada");
      return;
    }

    const needsValue = !["is_empty", "is_not_empty", "isTrue", "isFalse"].includes(operator);
    if (needsValue && !value.trim()) {
      alert("Por favor, defina o valor para comparação");
      return;
    }

    const formData = {
      variable: variable.trim(),
      operator,
      value: value.trim(),
      maxIterations: parseInt(maxIterations) || 100,
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: formData,
      });
    } else {
      onSave(formData);
    }
    handleClose();
  };

  const needsValue = !["is_empty", "is_not_empty", "isTrue", "isFalse"].includes(operator);
  const operatorLabel = operators.find(o => o.value === operator)?.label || operator;

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Autorenew className={classes.icon} />
          <Typography variant="h6">
            {open === "edit" ? "Editar While (Enquanto)" : "Novo While (Enquanto)"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.content} dividers>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          O While executa o fluxo conectado à saída "Loop" enquanto a condição for
          verdadeira. Quando a condição se torna falsa, segue pela saída "Fim".
        </Typography>

        {/* Variable */}
        <Box className={classes.section} mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Variável a avaliar *
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: ${contador} ou ${continuar}"
            value={variable}
            onChange={(e) => setVariable(e.target.value)}
            variant="outlined"
            size="small"
          />
          <Typography className={classes.helperText}>
            Use {"${variavel}"} para referenciar uma variável do fluxo.
          </Typography>
        </Box>

        <Divider style={{ margin: "16px 0" }} />

        {/* Operator */}
        <Box className={classes.section}>
          <Typography variant="subtitle2" gutterBottom>
            Operador *
          </Typography>
          <FormControl variant="outlined" size="small" fullWidth>
            <Select
              value={operator}
              onChange={(e) => setOperator(e.target.value)}
            >
              {operators.map((op) => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Value */}
        {needsValue && (
          <Box className={classes.section}>
            <Typography variant="subtitle2" gutterBottom>
              Valor para comparação *
            </Typography>
            <TextField
              fullWidth
              placeholder="Ex: 10, true, ${outraVariavel}"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              variant="outlined"
              size="small"
            />
            <Typography className={classes.helperText}>
              Pode ser um valor fixo ou uma variável.
            </Typography>
          </Box>
        )}

        <Divider style={{ margin: "16px 0" }} />

        {/* Max Iterations */}
        <Box className={classes.section}>
          <Typography variant="subtitle2" gutterBottom>
            Máximo de iterações (segurança)
          </Typography>
          <TextField
            fullWidth
            type="number"
            placeholder="100"
            value={maxIterations}
            onChange={(e) => setMaxIterations(e.target.value)}
            variant="outlined"
            size="small"
            inputProps={{ min: 1, max: 1000 }}
          />
          <Typography className={classes.helperText}>
            Limite para evitar loops infinitos. O loop para automaticamente após esse número.
          </Typography>
        </Box>

        {/* Info Box */}
        <Paper className={classes.infoBox} elevation={0}>
          <Info style={{ color: "#FF6F00", width: 20, height: 20, flexShrink: 0 }} />
          <Box>
            <Typography variant="body2" style={{ fontWeight: 500, marginBottom: 4 }}>
              Dicas de uso:
            </Typography>
            <Typography variant="caption" component="div" style={{ marginBottom: 2 }}>
              • Use com um nó de variável para controlar o loop
            </Typography>
            <Typography variant="caption" component="div" style={{ marginBottom: 2 }}>
              • Atualize a variável dentro do loop para evitar loop infinito
            </Typography>
            <Typography variant="caption" component="div">
              • Exemplo: while <span className={classes.variableChip}>{"${tentativas}"}</span> {"<"} 3
            </Typography>
          </Box>
        </Paper>

        {/* Preview */}
        <Paper className={classes.previewBox} elevation={0}>
          <Typography variant="subtitle2" gutterBottom>
            Prévia:
          </Typography>
          <Box style={{ fontFamily: "monospace", fontSize: "12px" }}>
            <div style={{ color: "#FF6F00", fontWeight: "bold" }}>
              while ({variable || "variavel"} {operatorLabel.toLowerCase()} {needsValue ? (value || "valor") : ""}) {"{"}
            </div>
            <div style={{ paddingLeft: 16, color: "#666" }}>
              // Saída "Loop" - executa enquanto verdadeiro
            </div>
            <div style={{ color: "#FF6F00", fontWeight: "bold" }}>{"}"}</div>
            <div style={{ color: "#4caf50", marginTop: 4 }}>
              // Saída "Fim" - quando condição é falsa
            </div>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          style={{ backgroundColor: "#FF6F00", color: "#fff" }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderAddWhileModal;
